import React, { useState, useEffect, useCallback } from "react";
import { BrainCircuit, Save, ToggleLeft, ToggleRight, AlertCircle, CheckCircle } from "lucide-react";
import { getAiPrompts, saveAiPrompt } from "shared/api/function_data";
import { getAllSidebarItems } from "./AdminConstants";
import { CollapsibleSection, FormField } from "./AdminShared";

/**
 * AiPromptsTab — Admin-only tab to enable/disable AI insights per report key.
 * Contributors edit their own prompts from the Upload page.
 */
function AiPromptsTab({ functionId, year, functionSidebar }) {
  const [prompts, setPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [result, setResult] = useState(null);

  // Get all report-type sidebar items
  const reportKeys = getAllSidebarItems(functionSidebar).filter((item) => item.type === "report");

  const loadPrompts = useCallback(async () => {
    if (!functionId || !year) return;
    setLoading(true);
    try {
      const data = await getAiPrompts(functionId, year);
      setPrompts(data || {});
    } catch {
      setPrompts({});
    } finally {
      setLoading(false);
    }
  }, [functionId, year]);

  useEffect(() => { loadPrompts(); }, [loadPrompts]);

  const handleToggle = async (key, currentEnabled) => {
    const newEnabled = !currentEnabled;
    setSaving(key);
    setResult(null);
    try {
      await saveAiPrompt(functionId, year, key, newEnabled, prompts[key]?.prompt || "", "");
      setPrompts((prev) => ({
        ...prev,
        [key]: { ...prev[key], enabled: newEnabled },
      }));
      setResult({ type: "success", message: `AI ${newEnabled ? "enabled" : "disabled"} for ${key}` });
    } catch (err) {
      setResult({ type: "error", message: err.message });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div className="admin-tab-content"><div className="admin-loading"><div className="admin-loading__spinner" /><p>Loading AI settings...</p></div></div>;
  }

  return (
    <div className="admin-tab-content">
      {/* Result */}
      {result && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "10px 14px", borderRadius: "8px", marginBottom: "12px",
          fontSize: "13px", fontWeight: 600,
          background: result.type === "success" ? "color-mix(in srgb, var(--positive) 8%, transparent)" : "color-mix(in srgb, var(--negative) 8%, transparent)",
          color: result.type === "success" ? "var(--positive)" : "var(--negative)",
        }}>
          {result.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          <span>{result.message}</span>
        </div>
      )}

      <CollapsibleSection
        title="AI Insights — Per Slide Toggle"
        subtitle="Enable AI analysis for specific report slides. Contributors set their own prompts in the Upload page."
      >
        <div style={{
          padding: "10px 14px", borderRadius: "8px", marginBottom: "16px",
          background: "color-mix(in srgb, var(--accent) 6%, transparent)",
          border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
          fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.6,
        }}>
          <strong>ℹ️ How it works:</strong> Enable AI for a slide → Contributor writes a prompt describing their metrics →
          On every PDF upload, AI reads the report and generates insights automatically.
          Insights appear on the Report page for all viewers.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {reportKeys.map(({ key, label }) => {
            const config = prompts[key] || {};
            const isEnabled = config.enabled || false;
            const hasPrompt = !!(config.prompt && config.prompt.trim());

            return (
              <div
                key={key}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 16px", borderRadius: "8px",
                  border: "1px solid var(--border)",
                  background: isEnabled ? "color-mix(in srgb, var(--positive) 4%, transparent)" : "var(--bg-primary)",
                  transition: "all 0.2s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <BrainCircuit size={16} style={{ color: isEnabled ? "var(--positive)" : "var(--text-muted)" }} />
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                      {isEnabled
                        ? hasPrompt ? `✅ Prompt set (by ${config.last_updated_by || "unknown"})` : "⚠️ Enabled but no prompt set yet"
                        : "Disabled"}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleToggle(key, isEnabled)}
                  disabled={saving === key}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "6px 14px", border: "1px solid var(--border)", borderRadius: "6px",
                    background: isEnabled ? "var(--positive)" : "var(--bg-primary)",
                    color: isEnabled ? "#fff" : "var(--text-secondary)",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600, fontFamily: "inherit",
                    transition: "all 0.2s",
                  }}
                >
                  {saving === key ? "..." : isEnabled ? <><ToggleRight size={14} /> On</> : <><ToggleLeft size={14} /> Off</>}
                </button>
              </div>
            );
          })}

          {reportKeys.length === 0 && (
            <div className="admin-empty-state admin-empty-state--sm">
              <p>No report-type sidebar items found. Add report items in Sidebar Config first.</p>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}

export default AiPromptsTab;
